import StarField from '../components/StarField';
import { useState } from 'react';
import SectionHeader from '../components/SectionHeader';
import Modal from '../components/Modal';
import { achievements } from '../data/achievements';
import { FaTrophy, FaMapMarkerAlt, FaCalendarAlt, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import styles from './Achievements.module.css';

function ImageCarousel({ images }) {
  const [currentIndex, setCurrentIndex] = useState(0);

  const goToPrevious = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    );
  };

  const goToNext = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === images.length - 1 ? 0 : prevIndex + 1
    );
  };

  if (!images || images.length === 0) {
    return (
      <div className={styles.noImages}>
        <p>Photos coming soon...</p>
      </div>
    );
  }

  return (
    <div className={styles.carousel}>
      <button 
        className={`${styles.carouselButton} ${styles.carouselButtonPrev}`}
        onClick={goToPrevious}
        aria-label="Previous image"
      >
        <FaChevronLeft />
      </button>

      <div className={styles.carouselImageContainer}>
        <img 
          src={images[currentIndex]} 
          alt={`Slide ${currentIndex + 1}`}
          className={styles.carouselImage}
          onError={(e) => {
            e.target.src = 'https://via.placeholder.com/800x400?text=Photo+Coming+Soon';
          }}
        />
      </div>

      <button 
        className={`${styles.carouselButton} ${styles.carouselButtonNext}`}
        onClick={goToNext}
        aria-label="Next image"
      >
        <FaChevronRight />
      </button>

      <div className={styles.carouselDots}>
        {images.map((_, index) => (
          <button
            key={index}
            className={`${styles.dot} ${index === currentIndex ? styles.dotActive : ''}`}
            onClick={() => setCurrentIndex(index)}
            aria-label={`Go to image ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
}

function Achievements() {
  const [selectedAchievement, setSelectedAchievement] = useState(null);

  const openModal = (achievement) => {
    setSelectedAchievement(achievement);
  };

  const closeModal = () => {
    setSelectedAchievement(null);
  };

  return (
    <div className={styles.achievements}>
      <StarField />
      <SectionHeader 
        title="Achievements" 
        subtitle="Competitions, awards and recognitions"
      />

      <div className={styles.grid}>
        {achievements.map((achievement) => (
          <div 
            key={achievement.id}
            className={`${styles.card} ${styles.clickable}`}
            onClick={() => openModal(achievement)}
          >
            <div className={styles.trophy}>
              <FaTrophy />
            </div>

            <div className={styles.category}>{achievement.category}</div>
            
            <h3 className={styles.title}>{achievement.title}</h3>
            
            <div className={styles.meta}>
              <span className={styles.metaItem}>
                <FaCalendarAlt /> {achievement.year}
              </span>
              {achievement.location && (
                <span className={styles.metaItem}>
                  <FaMapMarkerAlt /> {achievement.location}
                </span>
              )}
            </div>

            <p className={styles.shortDescription}>
              {achievement.shortDescription}
            </p>

            <span className={styles.clickHint}>Click for details →</span>
          </div>
        ))}
      </div>

      {selectedAchievement && (
        <Modal onClose={closeModal}>
          <div className={styles.modalHeader}>
            <div className={styles.modalTrophy}>
              <FaTrophy />
            </div>
            <div>
              <div className={styles.modalCategory}>
                {selectedAchievement.category}
              </div>
              <h2>{selectedAchievement.title}</h2>
              <div className={styles.modalMeta}>
                <span><FaCalendarAlt /> {selectedAchievement.year}</span>
                {selectedAchievement.location && (
                  <span><FaMapMarkerAlt /> {selectedAchievement.location}</span>
                )}
              </div>
            </div>
          </div>

          <div className={styles.modalContent}>
            <div className={styles.description}>
              {selectedAchievement.fullDescription.split('\n\n').map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </div>

            {selectedAchievement.skills && selectedAchievement.skills.length > 0 && (
              <div className={styles.skillsSection}>
                <h4>Skills & Competencies Demonstrated</h4>
                <ul className={styles.skillsList}>
                  {selectedAchievement.skills.map((skill, index) => (
                    <li key={index}>{skill}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className={styles.gallerySection}>
              <h4>Event Gallery</h4>
              <ImageCarousel images={selectedAchievement.images} />
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default Achievements;
